import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import SEO from './SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ROLES = [
  { value: 'wealth_advisor',       label: 'Wealth Advisor' },
  { value: 'family_office',        label: 'Family Office Principal' },
  { value: 'asset_manager',        label: 'Asset Manager' },
  { value: 'other',                label: 'Other' },
];

const INTEREST_AREAS = [
  { value: 'platform_overview',    label: 'Overview of the platform' },
  { value: 'partner_introductions',label: 'Introducing clients as a partner' },
  { value: 'integration_capability', label: 'Custody / data integration capability' },
  { value: 'security_review',      label: 'Security and privacy review' },
  { value: 'other',                label: 'Other' },
];

const CONTACT_CHANNELS = [
  { value: 'email',      label: 'Email' },
  { value: 'video_call', label: 'Video call' },
  { value: 'phone',      label: 'Phone' },
];

const BriefingPage = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: '', organisation: '', role: '', interest_area: '',
    contact_channel: 'email', email: '', notes: '',
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const canSubmit = form.name.trim() && form.organisation.trim() && form.role && form.interest_area && form.email.trim();

  const submit = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/briefings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(d.detail || 'Could not submit your request');
      }
      setSubmitted(true);
      toast.success('Briefing request received');
    } catch (e) {
      toast.error(e.message || 'Could not submit your request');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-linear-to-br from-gray-900 via-gray-800 to-black text-white">
      <SEO
        title="Request a Private Briefing"
        description="A private channel for wealth advisors, family-office principals and asset managers. Request a 30-minute briefing with the founder."
        path="/briefing"
      />
      <header className="border-b border-gray-700">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <Button variant="ghost" onClick={() => navigate('/')} className="text-gray-300 hover:text-white">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to Home
            </Button>
            <span className="text-xl font-bold">My<span className="text-green-600">Invest</span><span className="text-blue-600">Folio</span></span>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 sm:px-6 lg:px-8 py-16 max-w-2xl">
        {submitted ? (
          <div className="text-center bg-gray-800 border border-gray-700 rounded-xl p-12" data-testid="briefing-thanks">
            <h1 className="text-3xl font-bold text-emerald-400 mb-4">Thank you</h1>
            <p className="text-base text-gray-300 mb-8 max-w-md mx-auto leading-relaxed">
              Your briefing request has been received. We respond to every enquiry personally within five business days.
            </p>
            <Button onClick={() => navigate('/')} className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold px-8 py-3 rounded-lg">
              Return to Home
            </Button>
          </div>
        ) : (
          <>
            <div className="text-center mb-10">
              <h1 className="text-4xl sm:text-5xl font-bold mb-4 text-emerald-600">Request a Private Briefing</h1>
              <div className="w-24 h-1 mx-auto bg-linear-to-r from-emerald-500 to-blue-500 mb-6"></div>
              <p className="text-base text-gray-300 max-w-xl mx-auto leading-relaxed">
                A private channel for wealth advisors, family-office principals and asset managers who would like a 30-minute
                conversation with the founder before any commitment.
              </p>
            </div>

            <div className="bg-gray-800 border border-gray-700 rounded-xl p-8 space-y-5" data-testid="briefing-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <Label className="text-gray-200 text-sm">Full name</Label>
                  <Input value={form.name} onChange={(e) => update('name', e.target.value)}
                    className="mt-1 bg-gray-900 border-gray-700 text-white" data-testid="briefing-name" />
                </div>
                <div>
                  <Label className="text-gray-200 text-sm">Organisation</Label>
                  <Input value={form.organisation} onChange={(e) => update('organisation', e.target.value)}
                    className="mt-1 bg-gray-900 border-gray-700 text-white" data-testid="briefing-organisation" />
                </div>
              </div>

              <div>
                <Label className="text-gray-200 text-sm">Your role</Label>
                <select value={form.role} onChange={(e) => update('role', e.target.value)}
                  className="mt-1 w-full bg-gray-900 border border-gray-700 text-white rounded-md px-3 py-2 text-sm"
                  data-testid="briefing-role">
                  <option value="">Select your role</option>
                  {ROLES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>

              <div>
                <Label className="text-gray-200 text-sm">What would you like to discuss?</Label>
                <select value={form.interest_area} onChange={(e) => update('interest_area', e.target.value)}
                  className="mt-1 w-full bg-gray-900 border border-gray-700 text-white rounded-md px-3 py-2 text-sm"
                  data-testid="briefing-interest">
                  <option value="">Select an interest area</option>
                  {INTEREST_AREAS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                </select>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <Label className="text-gray-200 text-sm">Email</Label>
                  <Input type="email" value={form.email} onChange={(e) => update('email', e.target.value)}
                    className="mt-1 bg-gray-900 border-gray-700 text-white" data-testid="briefing-email" />
                </div>
                <div>
                  <Label className="text-gray-200 text-sm">Preferred contact</Label>
                  <select value={form.contact_channel} onChange={(e) => update('contact_channel', e.target.value)}
                    className="mt-1 w-full bg-gray-900 border border-gray-700 text-white rounded-md px-3 py-2 text-sm"
                    data-testid="briefing-contact-channel">
                    {CONTACT_CHANNELS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <Label className="text-gray-200 text-sm">Anything else (optional)</Label>
                <Textarea value={form.notes} onChange={(e) => update('notes', e.target.value.slice(0, 800))}
                  placeholder="Background on your firm, specific questions, scheduling preferences…"
                  rows={4} className="mt-1 bg-gray-900 border-gray-700 text-white text-sm"
                  data-testid="briefing-notes" />
                <p className="text-xs text-gray-500 mt-1">{form.notes.length}/800</p>
              </div>

              <Button onClick={submit} disabled={!canSubmit || submitting}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-3 rounded-lg"
                data-testid="briefing-submit">
                {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin inline" />Sending…</> : 'Request Briefing'}
              </Button>
            </div>

            <p className="text-xs text-gray-500 text-center mt-6 max-w-lg mx-auto">
              This is a private channel for professional intermediaries. For other enquiries,{' '}
              <button onClick={() => navigate('/?invite=1')} className="text-emerald-400 underline hover:text-emerald-300">
                please request an invitation
              </button>.
            </p>
          </>
        )}
      </main>
    </div>
  );
};

export default BriefingPage;
